import { useState, useCallback, useMemo, useRef } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Search,
  Network,
  Users,
  Building2,
  FileText,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Download,
  Filter,
  RefreshCw,
  Eye,
  Link2,
  TrendingUp,
  Globe,
  BookOpen,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Types for network visualization
interface NetworkNode {
  id: string;
  label: string;
  type: 'researcher' | 'institution' | 'publication' | 'dataset';
  size: number;
  color: string;
  x?: number;
  y?: number;
  citations?: number;
  connections?: number;
  year?: number;
}

interface NetworkEdge {
  source: string;
  target: string;
  weight: number;
  type: 'citation' | 'collaboration' | 'affiliation';
}

interface NetworkData {
  nodes: NetworkNode[];
  edges: NetworkEdge[];
}

// Sample network data representing Australian research connections
const sampleNetworkData: NetworkData = {
  nodes: [
    // Researchers
    { id: 'r1', label: 'Dr. Sarah Chen', type: 'researcher', size: 45, color: '#667eea', citations: 1250, connections: 28 },
    { id: 'r2', label: 'Prof. James Wilson', type: 'researcher', size: 55, color: '#667eea', citations: 2100, connections: 42 },
    { id: 'r3', label: 'Dr. Emily Zhang', type: 'researcher', size: 38, color: '#667eea', citations: 890, connections: 19 },
    { id: 'r4', label: 'Dr. Michael Brown', type: 'researcher', size: 42, color: '#667eea', citations: 1100, connections: 25 },
    { id: 'r5', label: 'Prof. Lisa Anderson', type: 'researcher', size: 50, color: '#667eea', citations: 1800, connections: 35 },
    { id: 'r6', label: 'Dr. David Kim', type: 'researcher', size: 35, color: '#667eea', citations: 720, connections: 15 },
    { id: 'r7', label: 'Dr. Rachel Green', type: 'researcher', size: 40, color: '#667eea', citations: 980, connections: 22 },
    { id: 'r8', label: 'Prof. Thomas Lee', type: 'researcher', size: 48, color: '#667eea', citations: 1450, connections: 31 },

    // Institutions
    { id: 'i1', label: 'CSIRO', type: 'institution', size: 70, color: '#48bb78', connections: 156 },
    { id: 'i2', label: 'University of Melbourne', type: 'institution', size: 65, color: '#48bb78', connections: 142 },
    { id: 'i3', label: 'AIMS', type: 'institution', size: 55, color: '#48bb78', connections: 98 },
    { id: 'i4', label: 'Swinburne University', type: 'institution', size: 50, color: '#48bb78', connections: 87 },
    { id: 'i5', label: 'UNSW Sydney', type: 'institution', size: 60, color: '#48bb78', connections: 125 },

    // Publications
    { id: 'p1', label: 'Climate Change Impacts on GBR', type: 'publication', size: 30, color: '#ed8936', citations: 450, year: 2023 },
    { id: 'p2', label: 'ML for Coral Detection', type: 'publication', size: 28, color: '#ed8936', citations: 320, year: 2023 },
    { id: 'p3', label: 'Ocean Temperature Analysis', type: 'publication', size: 32, color: '#ed8936', citations: 580, year: 2022 },
    { id: 'p4', label: 'Biodiversity Assessment Methods', type: 'publication', size: 25, color: '#ed8936', citations: 210, year: 2024 },
    { id: 'p5', label: 'Satellite Imagery Processing', type: 'publication', size: 27, color: '#ed8936', citations: 290, year: 2023 },

    // Datasets
    { id: 'd1', label: 'GBR Temperature Dataset', type: 'dataset', size: 35, color: '#9f7aea', citations: 89, year: 2023 },
    { id: 'd2', label: 'Coral Cover Survey 2020-2024', type: 'dataset', size: 38, color: '#9f7aea', citations: 124, year: 2024 },
    { id: 'd3', label: 'Marine Species Database', type: 'dataset', size: 40, color: '#9f7aea', citations: 156, year: 2022 },
  ],
  edges: [
    // Researcher collaborations
    { source: 'r1', target: 'r2', weight: 5, type: 'collaboration' },
    { source: 'r1', target: 'r3', weight: 3, type: 'collaboration' },
    { source: 'r2', target: 'r4', weight: 4, type: 'collaboration' },
    { source: 'r2', target: 'r5', weight: 6, type: 'collaboration' },
    { source: 'r3', target: 'r6', weight: 2, type: 'collaboration' },
    { source: 'r4', target: 'r7', weight: 3, type: 'collaboration' },
    { source: 'r5', target: 'r8', weight: 4, type: 'collaboration' },
    { source: 'r6', target: 'r7', weight: 2, type: 'collaboration' },
    { source: 'r7', target: 'r8', weight: 3, type: 'collaboration' },
    { source: 'r1', target: 'r5', weight: 2, type: 'collaboration' },

    // Institution affiliations
    { source: 'r1', target: 'i1', weight: 1, type: 'affiliation' },
    { source: 'r2', target: 'i2', weight: 1, type: 'affiliation' },
    { source: 'r3', target: 'i3', weight: 1, type: 'affiliation' },
    { source: 'r4', target: 'i1', weight: 1, type: 'affiliation' },
    { source: 'r5', target: 'i4', weight: 1, type: 'affiliation' },
    { source: 'r6', target: 'i5', weight: 1, type: 'affiliation' },
    { source: 'r7', target: 'i2', weight: 1, type: 'affiliation' },
    { source: 'r8', target: 'i3', weight: 1, type: 'affiliation' },

    // Publication citations
    { source: 'p1', target: 'p3', weight: 3, type: 'citation' },
    { source: 'p2', target: 'p1', weight: 2, type: 'citation' },
    { source: 'p4', target: 'p1', weight: 1, type: 'citation' },
    { source: 'p4', target: 'p3', weight: 2, type: 'citation' },
    { source: 'p5', target: 'p2', weight: 1, type: 'citation' },

    // Researcher publications
    { source: 'r1', target: 'p1', weight: 1, type: 'citation' },
    { source: 'r2', target: 'p1', weight: 1, type: 'citation' },
    { source: 'r3', target: 'p2', weight: 1, type: 'citation' },
    { source: 'r4', target: 'p3', weight: 1, type: 'citation' },
    { source: 'r5', target: 'p4', weight: 1, type: 'citation' },
    { source: 'r6', target: 'p5', weight: 1, type: 'citation' },

    // Dataset usage
    { source: 'p1', target: 'd1', weight: 2, type: 'citation' },
    { source: 'p2', target: 'd2', weight: 1, type: 'citation' },
    { source: 'p3', target: 'd1', weight: 2, type: 'citation' },
    { source: 'p3', target: 'd3', weight: 1, type: 'citation' },
    { source: 'p4', target: 'd2', weight: 1, type: 'citation' },
    { source: 'p4', target: 'd3', weight: 2, type: 'citation' },
  ],
};

// Statistics for the network
const networkStats = {
  totalNodes: 21,
  totalEdges: 32,
  avgConnections: 4.8,
  clusters: 3,
  density: 0.15,
  mostConnected: 'Prof. James Wilson',
  mostCited: 'Ocean Temperature Analysis',
  strongestCollaboration: 'CSIRO - University of Melbourne',
};

// Node type configuration
const nodeTypeConfig = {
  researcher: { icon: Users, color: 'bg-indigo-500', label: 'Researchers' },
  institution: { icon: Building2, color: 'bg-green-500', label: 'Institutions' },
  publication: { icon: FileText, color: 'bg-orange-500', label: 'Publications' },
  dataset: { icon: BookOpen, color: 'bg-purple-500', label: 'Datasets' },
};

// Simple force-directed layout simulation
function useForceLayout(data: NetworkData, width: number, height: number) {
  return useMemo(() => {
    const nodes = data.nodes.map((node, index) => {
      // Distribute nodes in a circular pattern based on type
      const typeIndex = ['researcher', 'institution', 'publication', 'dataset'].indexOf(node.type);
      const nodesOfType = data.nodes.filter(n => n.type === node.type);
      const indexInType = nodesOfType.indexOf(node);
      const angleOffset = (typeIndex * Math.PI) / 2;
      const angle = angleOffset + (indexInType / nodesOfType.length) * Math.PI / 2;
      const radius = 120 + typeIndex * 80;

      return {
        ...node,
        x: width / 2 + Math.cos(angle) * radius + (Math.random() - 0.5) * 40,
        y: height / 2 + Math.sin(angle) * radius + (Math.random() - 0.5) * 40,
      };
    });

    return { nodes, edges: data.edges };
  }, [data, width, height]);
}

export function ResearchNetworkPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedNodeType, setSelectedNodeType] = useState<string>('all');
  const [selectedNode, setSelectedNode] = useState<NetworkNode | null>(null);
  const [zoom, setZoom] = useState(1);
  const [viewMode, setViewMode] = useState<'network' | 'list'>('network');

  // Pan state for dragging
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const svgRef = useRef<SVGSVGElement>(null);

  // Canvas dimensions
  const canvasWidth = 800;
  const canvasHeight = 600;

  // Get force-directed layout
  const layoutData = useForceLayout(sampleNetworkData, canvasWidth, canvasHeight);

  // Filter nodes based on search and type
  const filteredNodes = useMemo(() => {
    return layoutData.nodes.filter(node => {
      const matchesSearch = node.label.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = selectedNodeType === 'all' || node.type === selectedNodeType;
      return matchesSearch && matchesType;
    });
  }, [layoutData.nodes, searchQuery, selectedNodeType]);

  // Filter edges to only show connections between visible nodes
  const filteredEdges = useMemo(() => {
    const visibleNodeIds = new Set(filteredNodes.map(n => n.id));
    return layoutData.edges.filter(
      edge => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target)
    );
  }, [layoutData.edges, filteredNodes]);

  const handleNodeClick = useCallback((node: NetworkNode) => {
    setSelectedNode(node);
  }, []);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.2, 2));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Mouse handlers for panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) { // Left mouse button
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  }, [pan]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Network className="h-8 w-8 text-primary" />
            Citation Network
          </h1>
          <p className="text-muted-foreground mt-1">
            Visualize research connections, collaborations, and citation relationships
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export Graph
          </Button>
          <Button variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Globe className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{networkStats.totalNodes}</p>
                <p className="text-xs text-muted-foreground">Total Nodes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-green-500/10">
                <Link2 className="h-4 w-4 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{networkStats.totalEdges}</p>
                <p className="text-xs text-muted-foreground">Connections</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <TrendingUp className="h-4 w-4 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{networkStats.avgConnections}</p>
                <p className="text-xs text-muted-foreground">Avg. Connections</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Sparkles className="h-4 w-4 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{networkStats.clusters}</p>
                <p className="text-xs text-muted-foreground">Clusters</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-indigo-500/10">
                <Users className="h-4 w-4 text-indigo-500" />
              </div>
              <div>
                <p className="text-sm font-semibold truncate">{networkStats.mostConnected}</p>
                <p className="text-xs text-muted-foreground">Most Connected</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Network Visualization */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Research Network Graph</CardTitle>
                <CardDescription>
                  Interactive visualization of research relationships
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={handleZoomOut}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground w-12 text-center">
                  {Math.round(zoom * 100)}%
                </span>
                <Button variant="outline" size="icon" onClick={handleZoomIn}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={handleResetZoom}>
                  <Maximize2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search nodes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={selectedNodeType} onValueChange={setSelectedNodeType}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="researcher">Researchers</SelectItem>
                  <SelectItem value="institution">Institutions</SelectItem>
                  <SelectItem value="publication">Publications</SelectItem>
                  <SelectItem value="dataset">Datasets</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-4 mb-4 p-3 bg-muted/50 rounded-lg">
              <span className="text-sm font-medium">Legend:</span>
              {Object.entries(nodeTypeConfig).map(([type, config]) => (
                <div key={type} className="flex items-center gap-2">
                  <div className={cn('w-3 h-3 rounded-full', config.color)} />
                  <span className="text-sm text-muted-foreground">{config.label}</span>
                </div>
              ))}
            </div>

            {/* SVG Network Graph */}
            <div
              className="border rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 overflow-hidden relative"
              style={{ height: '600px', cursor: isDragging ? 'grabbing' : 'grab' }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            >
              <svg
                ref={svgRef}
                width="100%"
                height="100%"
                viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
                preserveAspectRatio="xMidYMid meet"
                style={{ display: 'block' }}
              >
                <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`} style={{ transformOrigin: 'center center' }}>
                {/* Edges */}
                <g className="edges">
                  {filteredEdges.map((edge, index) => {
                    const sourceNode = filteredNodes.find(n => n.id === edge.source);
                    const targetNode = filteredNodes.find(n => n.id === edge.target);
                    if (!sourceNode || !targetNode) return null;

                    const edgeColor = edge.type === 'citation' ? '#ed8936' :
                                     edge.type === 'collaboration' ? '#667eea' : '#48bb78';

                    return (
                      <line
                        key={`edge-${index}`}
                        x1={sourceNode.x}
                        y1={sourceNode.y}
                        x2={targetNode.x}
                        y2={targetNode.y}
                        stroke={edgeColor}
                        strokeWidth={edge.weight * 0.5 + 0.5}
                        strokeOpacity={0.4}
                        className="transition-opacity hover:opacity-100"
                      />
                    );
                  })}
                </g>

                {/* Nodes */}
                <g className="nodes">
                  {filteredNodes.map((node) => (
                    <g
                      key={node.id}
                      transform={`translate(${node.x}, ${node.y})`}
                      className="cursor-pointer transition-transform hover:scale-110"
                      onClick={() => handleNodeClick(node)}
                    >
                      <circle
                        r={node.size / 2}
                        fill={node.color}
                        fillOpacity={selectedNode?.id === node.id ? 1 : 0.8}
                        stroke={selectedNode?.id === node.id ? '#fff' : 'transparent'}
                        strokeWidth={3}
                        className="transition-all"
                      />
                      <text
                        y={node.size / 2 + 14}
                        textAnchor="middle"
                        className="text-[10px] fill-current font-medium"
                        style={{ fontSize: '10px' }}
                      >
                        {node.label.length > 20 ? node.label.slice(0, 20) + '...' : node.label}
                      </text>
                    </g>
                  ))}
                </g>
                </g>
              </svg>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar - Node Details */}
        <div className="space-y-6">
          {/* Selected Node Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Node Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedNode ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: selectedNode.color }}
                    >
                      {(() => {
                        const Icon = nodeTypeConfig[selectedNode.type].icon;
                        return <Icon className="h-6 w-6 text-white" />;
                      })()}
                    </div>
                    <div>
                      <p className="font-semibold">{selectedNode.label}</p>
                      <Badge variant="secondary" className="text-xs capitalize">
                        {selectedNode.type}
                      </Badge>
                    </div>
                  </div>

                  <div className="space-y-2 pt-2 border-t">
                    {selectedNode.citations !== undefined && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Citations</span>
                        <span className="font-medium">{selectedNode.citations.toLocaleString()}</span>
                      </div>
                    )}
                    {selectedNode.connections !== undefined && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Connections</span>
                        <span className="font-medium">{selectedNode.connections}</span>
                      </div>
                    )}
                    {selectedNode.year && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Year</span>
                        <span className="font-medium">{selectedNode.year}</span>
                      </div>
                    )}
                  </div>

                  <Button className="w-full" size="sm">
                    View Full Profile
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Click on a node to view details
                </p>
              )}
            </CardContent>
          </Card>

          {/* Network Insights */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5" />
                AI Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg">
                <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
                  Strong Collaboration Cluster
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  CSIRO and University of Melbourne show the strongest cross-institutional research ties
                </p>
              </div>
              <div className="p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg">
                <p className="text-sm font-medium text-orange-700 dark:text-orange-300">
                  Emerging Research Hub
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Climate change research has 45% more citations than average
                </p>
              </div>
              <div className="p-3 bg-green-50 dark:bg-green-950/30 rounded-lg">
                <p className="text-sm font-medium text-green-700 dark:text-green-300">
                  Dataset Reuse Opportunity
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  GBR Temperature Dataset is underutilized - 3 potential new applications identified
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Network Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Network Density</span>
                  <Badge variant="outline">{(networkStats.density * 100).toFixed(1)}%</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Avg Clustering</span>
                  <Badge variant="outline">0.42</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Path Length</span>
                  <Badge variant="outline">2.3 hops</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Modularity</span>
                  <Badge variant="outline">0.68</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default ResearchNetworkPage;
